import * as THREE from 'three';
import { hasTerrainPicking, pickTerrainGround } from './terrain-picking.js';

/** One prop, or one instance of an instanced prop, so the index prunes a forest batch down to the trees near the ray. */
interface Surface { mesh: THREE.Mesh; bounds: THREE.Box3; centre: THREE.Vector3; instance?: THREE.Matrix4 }
interface Branch { bounds: THREE.Box3; surfaces?: Surface[]; left?: Branch; right?: Branch }

// A modest approach buffer, independent of the much larger visible opening.
const APPROACH = [[1, 0], [.707, .707], [0, 1], [-.707, .707], [-1, 0], [-.707, -.707], [0, -1], [.707, -.707]];
/** Scenery opens only once it stands this far (metres, on the ground plan) between the hero and the camera; the shader ramps across it. */
export const FRONT_START = .1, FRONT_FULL = .4;
/** Terrain opens only this far above the hero's floor; lower ground stays solid. */
export const FLOOR_START = .15, FLOOR_FULL = .45;
// Probes run at most this often per hero while it or the camera moves, and refresh this often when nothing does. The opening's fade hides the lag.
const MOVING_REFRESH = 100, STILL_REFRESH = 250;

/** Horizontal direction towards an orthographic or perspective camera, falling back to its back vector when it looks straight down. */
export function towardCamera(camera: THREE.Camera, target: THREE.Vector3) {
  target.setFromMatrixColumn(camera.matrixWorld, 2); target.y = 0;
  if (target.lengthSq() < 1e-6) target.setFromMatrixColumn(camera.matrixWorld, 2);
  return target.normalize();
}

/** Camera visibility only: never shared with the server's vision or navigation rules. */
export class SceneryOcclusion {
  private terrain: THREE.Mesh | null = null;
  private surfaces: Surface[] = [];
  private added = new WeakSet<THREE.Mesh>();
  private tree: Branch | null = null;
  private raycaster = new THREE.Raycaster();
  private hits: THREE.Intersection[] = [];
  private sample = new THREE.Vector3();
  private view = new THREE.Vector3();
  private direction = new THREE.Vector3();
  private toward = new THREE.Vector3();
  private hero = new THREE.Vector3();
  private proxy = new THREE.Mesh();
  private cache = new WeakMap<THREE.Vector3, { key: string; flags: number; at: number }>();

  setTerrain(mesh: THREE.Mesh) { this.terrain = mesh; this.cache = new WeakMap(); }
  add(mesh: THREE.Mesh) {
    if (this.added.has(mesh)) return;
    this.added.add(mesh); mesh.updateWorldMatrix(true, false);
    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
    if (mesh instanceof THREE.InstancedMesh) {
      for (let index = 0; index < mesh.count; index++) {
        const instance = new THREE.Matrix4();
        mesh.getMatrixAt(index, instance); instance.premultiply(mesh.matrixWorld);
        this.push(mesh, mesh.geometry.boundingBox!.clone().applyMatrix4(instance), instance);
      }
    } else this.push(mesh, mesh.geometry.boundingBox!.clone().applyMatrix4(mesh.matrixWorld));
  }

  private push(mesh: THREE.Mesh, bounds: THREE.Box3, instance?: THREE.Matrix4) {
    // Small ground dressing cannot hide a torso; keep it out of the raycast index.
    if (bounds.isEmpty() || bounds.max.y - bounds.min.y < .75) return;
    bounds.expandByScalar(.3);
    this.surfaces.push({ mesh, bounds, centre: bounds.getCenter(new THREE.Vector3()), instance }); this.tree = null;
  }

  prepare() { if (!this.tree && this.surfaces.length) this.tree = branch(this.surfaces.slice()); }

  /** Bit 1: terrain blocks the body or its approach buffer; bit 2: a prop does. Only scenery in front of the hero counts. */
  flags(camera: THREE.Camera, position: THREE.Vector3, height: number, now = performance.now()) {
    const key = [position.x, position.y, position.z, height, ...camera.matrixWorld.elements].map(value => Math.round(value * 10)).join(',');
    const cached = this.cache.get(position);
    // Camera shake and walking change the key every frame; a short refresh interval keeps probing cheap and steady.
    if (cached && now >= cached.at && now - cached.at < (cached.key === key ? STILL_REFRESH : MOVING_REFRESH)) return cached.flags;
    this.prepare();
    const wanted = (this.terrain ? 1 : 0) | (this.tree ? 2 : 0);
    let flags = 0;
    this.direction.setFromMatrixColumn(camera.matrixWorld, 2).normalize().negate();
    towardCamera(camera, this.toward); this.hero.copy(position);
    for (const share of [.2, .6, .95]) {
      this.sample.copy(position); this.sample.y += height * share;
      flags = this.probe(camera, flags);
      if (flags === wanted) break;
    }
    // Open before the torso disappears, leaving room to click round the ridge's edge.
    // Keep the probes above the hero's own floor; the shader still preserves lower terrain.
    for (const [x, z] of APPROACH) {
      if (flags === wanted) break;
      this.sample.set(position.x + x * height * .75, position.y + height * .35, position.z + z * height * .75);
      flags = this.probe(camera, flags);
    }
    this.cache.set(position, { key, flags, at: now });
    return flags;
  }

  private probe(camera: THREE.Camera, flags: number) {
    this.view.copy(this.sample).applyMatrix4(camera.matrixWorldInverse);
    const near = (camera as THREE.OrthographicCamera).near ?? .1, distance = -near - this.view.z;
    if (distance <= .25) return flags;
    this.view.z = -near; this.view.applyMatrix4(camera.matrixWorld);
    this.raycaster.set(this.view, this.direction); this.raycaster.near = 0; this.raycaster.far = distance - .25;
    if (!(flags & 1) && this.terrain && this.intersects(this.terrain, true)) flags |= 1;
    if (!(flags & 2) && this.tree && this.blocked(this.tree)) flags |= 2;
    return flags;
  }

  /**
   * Whether the mesh is hit in front of the hero, where the shader would open it. Hits nearer the camera lie further in front
   * and higher, so the first hit decides. A tree on the slope just behind a hero can block an approach probe, but it never opens.
   */
  private intersects(mesh: THREE.Mesh, terrain = false) {
    let hit: THREE.Vector3 | null | undefined;
    // The ground is hundreds of thousands of triangles: walk only the lattice cells under the ray. A flat ground has no lattice
    // and cannot rise between the camera and a hero standing on it.
    if (terrain) hit = hasTerrainPicking(mesh) ? pickTerrainGround(mesh, this.raycaster) : null;
    else { this.hits.length = 0; this.raycaster.intersectObject(mesh, false, this.hits); hit = this.hits[0]?.point; }
    if (!hit) return false;
    if (terrain && hit.y - this.hero.y < FLOOR_START) return false;
    return (hit.x - this.hero.x) * this.toward.x + (hit.y - this.hero.y) * this.toward.y + (hit.z - this.hero.z) * this.toward.z > FRONT_START;
  }
  private blocked(node: Branch): boolean {
    if (!this.raycaster.ray.intersectsBox(node.bounds)) return false;
    if (node.surfaces) return node.surfaces.some(({ mesh, bounds, instance }) => {
      for (let object: THREE.Object3D | null = mesh; object; object = object.parent) if (!object.visible) return false;
      if (!this.raycaster.ray.intersectsBox(bounds)) return false;
      if (!instance) return this.intersects(mesh);
      const proxy = this.proxy;
      proxy.geometry = mesh.geometry; proxy.material = mesh.material; proxy.matrixWorld.copy(instance); proxy.layers.mask = mesh.layers.mask;
      return this.intersects(proxy);
    });
    return !!(node.left && this.blocked(node.left) || node.right && this.blocked(node.right));
  }
}

function branch(surfaces: Surface[]): Branch {
  const bounds = new THREE.Box3(); for (const surface of surfaces) bounds.union(surface.bounds);
  if (surfaces.length <= 8) return { bounds, surfaces };
  const size = bounds.getSize(new THREE.Vector3()), axis = size.x >= size.z && size.x >= size.y ? 'x' : size.z >= size.y ? 'z' : 'y';
  surfaces.sort((a, b) => a.centre[axis] - b.centre[axis]);
  const middle = Math.floor(surfaces.length / 2);
  return { bounds, left: branch(surfaces.slice(0, middle)), right: branch(surfaces.slice(middle)) };
}
